"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { FilterCountBadge } from "./filter-drawer"
import type { FilterItem } from "@/app/features/reports/utils/entities"
import type { FilterCounts, Filters, FilterTypes, SelectedFilters } from "../types/filters"
import extractFilterCount, { extractCountForKey } from "../utils/extractFilterCount"
import CountPill from "@/app/features/reports/components/excluded-count-pill"
import type { Count } from "@/app/features/reports/utils/calculateFilterCounts"

type ListItemProps = {
  item: Pick<FilterItem, "name"> // Use FilterItem type
  isSelected: boolean
  counts: Count
  customComponent?: React.ReactNode
  className?: string
  searchTerm?: string
  highlightSearchTerm?: (text: string, term: string) => React.ReactNode
}

const ListItem: React.FC<ListItemProps> = React.memo(
  ({ item, isSelected, counts, customComponent, className, searchTerm = "", highlightSearchTerm = (text) => text }) => {
    return (
      <div className="flex-1 flex items-center justify-between px-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "text-xs font-medium space-x-1 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 truncate max-w-[150px]",
                  className,
                )}
              >
                {highlightSearchTerm(item.name, searchTerm)}{" "}
                <span className="text-foreground font-light text-xs">({counts?.total ?? 0})</span>
                {customComponent ?? null}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{item.name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex flex-row gap-1 items-end justify-center">
          <CountPill count={counts?.unmatchedSegments ?? 0} type="fastfill" />
          <CountPill count={counts?.missingData ?? 0} type="missing" />
          <CountPill count={counts?.status ?? 0} type="status" />
          <CountPill count={counts?.excluded ?? 0} type="excluded" />
          <div className="w-10 flex items-end justify-end">
            <FilterCountBadge
              variant={isSelected ? "selected" : (counts?.total ?? 0 > 0) ? "partial" : "unselected"}
              className="bg-zinc-300 py-0.5 font-normal text-foreground"
            >
              {counts?.filtered ?? 0}
            </FilterCountBadge>
          </div>
        </div>
      </div>
    )
  },
)
ListItem.displayName = "ListItem"

const FilterCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    isPartial?: boolean
  }
>(({ className, isPartial, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-none border border-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
      {isPartial ? (
        <div className="h-4 w-4 flex items-center justify-center">
          <div className="h-2 w-2 bg-primary rounded-sm" />
        </div>
      ) : props.checked ? (
        <Check className="h-4 w-4" />
      ) : null}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
FilterCheckbox.displayName = "FilterCheckbox"

/**
 * Props for the PopupFacetSelector component
 * @typedef {Object} PopupFacetSelectorProps
 * @property {FilterTypes} name - Unique identifier for the selector
 * @property {string} description - Human-readable description of the selector
 * @property {Filters} allFiltersData - Hierarchical data structure containing the selectable items
 * @property {SelectedFilters} selectedFilters - Currently selected filters
 * @property {function} onStateChange - Callback function when selection changes (passes category and full new array of selected IDs)
 * @property {FilterCounts} [filterCounts] - Optional counts for filtering
 * @property {FilterCounts} [filteredCounts] - Optional counts for filtered items
 * @property {function} [renderCustom] - Optional custom renderer for items
 * @property {string} [storageKey] - Optional key for persisting selections in localStorage
 */
export interface PopupFacetSelectorProps {
  name: FilterTypes
  description: string
  allFiltersData: Filters | undefined
  selectedFilters: SelectedFilters
  onStateChange: (category: FilterTypes, newSelectedIds: string[]) => void
  filterCounts?: FilterCounts
  filteredCounts?: FilterCounts
  renderCustom?: (item: FilterItem) => React.ReactNode
  storageKey?: string
}

/**
 * PopupFacetSelector - A hierarchical selection component with popup interface
 *
 * This component provides a hierarchical selection interface with search capabilities,
 * displayed in a popup overlay. It supports nested items, partial selection states,
 * and various filtering options.
 *
 * @param {PopupFacetSelectorProps} props - Component props
 * @returns {React.ReactElement} The rendered component
 */
export function PopupFacetSelector({
  name,
  description,
  allFiltersData,
  selectedFilters,
  onStateChange: onStateChangeProp,
  filterCounts,
  filteredCounts,
  renderCustom,
  storageKey,
}: PopupFacetSelectorProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPath, setCurrentPath] = useState<{ id: string; name: string }[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set([]))

  const data = useMemo(() => allFiltersData?.[name] ?? [], [allFiltersData, name]);

  // Persist selections to localStorage when they change
  useEffect(() => {
    if (storageKey && internalSelectedIds.size > 0) {
      localStorage.setItem(
        `popup-facet-selector-${storageKey}-${name}`,
        JSON.stringify(Array.from(internalSelectedIds)),
      )
    }
  }, [internalSelectedIds, name, storageKey])

  // Load persisted selections on mount
  useEffect(() => {
    if (storageKey) {
      const savedSelections = localStorage.getItem(`popup-facet-selector-${storageKey}-${name}`)
      if (savedSelections) {
        try {
          const parsedSelections = JSON.parse(savedSelections)
          if (Array.isArray(parsedSelections) && parsedSelections.length > 0) {
            setInternalSelectedIds(new Set(parsedSelections))
            onStateChangeProp(name, parsedSelections)
          }
        } catch (e) {
          console.error("Error loading persisted selections:", e)
        }
      }
    }
  }, [name, onStateChangeProp, storageKey])

  const findItemById = useCallback((items: FilterItem[], id: string): FilterItem | null => {
    for (const item of items) {
      if (item.id === id) return item
      if (item.children) {
        const found = findItemById(item.children, id)
        if (found) return found
      }
    }
    return null
  }, [])

  const onSelectionChange = useCallback(
    (selectedIds: string[]) => {
      onStateChangeProp(name, selectedIds)
    },
    [onStateChangeProp, name],
  )

  const expandSelectedIds = useCallback((items: FilterItem[], selectedSet: Set<string>): Set<string> => {
    const expandedSet = new Set(selectedSet)
    const traverse = (items: FilterItem[]) => {
      for (const item of items) {
        if (selectedSet.has(item.id)) {
          const addAllDescendants = (subItems: FilterItem[]) => {
            for (const subItem of subItems) {
              expandedSet.add(subItem.id)
              if (subItem.children) {
                addAllDescendants(subItem.children)
              }
            }
          }
          if (item.children) {
            addAllDescendants(item.children)
          }
        } else if (item.children) {
          traverse(item.children)
        }
      }
    }
    traverse(items)
    return new Set([...expandedSet].filter((item) => item))
  }, [])

  useEffect(() => {
    const currentSelectionArray = selectedFilters[name] ?? [];
    const expandedSelectedIds = expandSelectedIds(data, new Set(currentSelectionArray));
    setInternalSelectedIds(expandedSelectedIds)
  }, [data, name, selectedFilters, expandSelectedIds]);

  const isItemSelected = useCallback(
    (item: FilterItem): boolean => {
      return internalSelectedIds.has(item.id)
    },
    [internalSelectedIds],
  )

  const hasSelectedDescendant = useCallback(
    (item: FilterItem): boolean => {
      if (item.children) {
        return item.children.some((child) => isItemSelected(child) || hasSelectedDescendant(child))
      }
      return false
    },
    [isItemSelected],
  )

  const isPartiallySelected = useCallback(
    (item: FilterItem): boolean => {
      return !isItemSelected(item) && hasSelectedDescendant(item)
    },
    [isItemSelected, hasSelectedDescendant],
  )

  const getMostInclusiveSelectedItems = useCallback((items: FilterItem[], selectedSet: Set<string>): string[] => {
    const result: string[] = []
    const traverse = (items: FilterItem[]) => {
      for (const item of items) {
        if (selectedSet.has(item.id)) {
          result.push(item.id)
          continue
        }
        if (item.children) {
          traverse(item.children)
        }
      }
    }
    traverse(items)
    return result
  }, [])

  const handleSelect = useCallback(
    (id: string) => {
      const newSelectedIds = new Set(internalSelectedIds)
      const item = findItemById(data, id)

      const toggleSelection = (items: FilterItem[], select: boolean) => {
        items.forEach((item) => {
          if (select) {
            newSelectedIds.add(item.id)
          } else {
            newSelectedIds.delete(item.id)
          }
          if (item.children) {
            toggleSelection(item.children, select)
          }
        })
      }

      if (item) {
        const isCurrentlySelected = newSelectedIds.has(id)
        toggleSelection([item], !isCurrentlySelected)

        const updateParentSelection = (items: FilterItem[], targetId: string) => {
          for (const item of items) {
            if (item.children && item.children.some((child) => child.id === targetId)) {
              const allChildrenSelected = item.children.every((child) => newSelectedIds.has(child.id))
              const someChildrenSelected = item.children.some(
                (child) => newSelectedIds.has(child.id) || hasSelectedDescendant(child),
              )

              if (allChildrenSelected) {
                newSelectedIds.add(item.id)
              } else if (someChildrenSelected) {
                newSelectedIds.delete(item.id)
              } else {
                newSelectedIds.delete(item.id)
              }

              updateParentSelection(data, item.id)
              break
            } else if (item.children) {
              updateParentSelection(item.children, targetId)
            }
          }
        }

        updateParentSelection(data, id)
      }

      setInternalSelectedIds(newSelectedIds)
      onSelectionChange(getMostInclusiveSelectedItems(data, newSelectedIds))
    },
    [data, internalSelectedIds, onSelectionChange, findItemById, hasSelectedDescendant, getMostInclusiveSelectedItems],
  )

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const newExpandedIds = new Set(prev)
      if (newExpandedIds.has(id)) {
        newExpandedIds.delete(id)
      } else {
        newExpandedIds.add(id)
      }
      return newExpandedIds
    })
  }, [])

  const selectAll = useCallback(() => {
    const allIds = new Set<string>()
    const collectIds = (items: FilterItem[]) => {
      items.forEach((item) => {
        allIds.add(item.id)
        if (item.children) collectIds(item.children)
      })
    }
    collectIds(data)
    setInternalSelectedIds(allIds)
    onSelectionChange(getMostInclusiveSelectedItems(data, allIds))
  }, [data, onSelectionChange, getMostInclusiveSelectedItems])

  const deselectAll = useCallback(() => {
    setInternalSelectedIds(new Set())
    onSelectionChange([])
  }, [onSelectionChange])

  const filteredData = useMemo(() => {
    if (!searchTerm) return data
    const filterItems = (items: FilterItem[]): FilterItem[] => {
      return items.reduce((acc: FilterItem[], item) => {
        if (item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          acc.push(item)
        } else if (item.children) {
          const filteredChildren = filterItems(item.children)
          if (filteredChildren.length > 0) {
            acc.push({ ...item, children: filteredChildren })
          }
        }
        return acc
      }, [])
    }
    return filterItems(data)
  }, [data, searchTerm])

  const selectAllVisible = useCallback(() => {
    const visibleIds = new Set<string>()
    const collectVisibleIds = (items: FilterItem[]) => {
      items.forEach((item) => {
        visibleIds.add(item.id)
        if (item.children) collectVisibleIds(item.children)
      })
    }
    collectVisibleIds(filteredData)

    const newSelectedIds = new Set(internalSelectedIds)
    visibleIds.forEach((id) => newSelectedIds.add(id))

    setInternalSelectedIds(newSelectedIds)
    onSelectionChange(getMostInclusiveSelectedItems(data, newSelectedIds))
  }, [data, internalSelectedIds, onSelectionChange, getMostInclusiveSelectedItems, filteredData])

  const expandAll = useCallback(() => {
    const allIds = new Set<string>()
    const collectIds = (items: FilterItem[]) => {
      items.forEach((item) => {
        if (item.children) {
          allIds.add(item.id)
          collectIds(item.children)
        }
      })
    }
    collectIds(data)
    setExpandedIds(allIds)
  }, [data])

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set())
  }, [])

  const toggleRootSelection = useCallback(() => {
    if (internalSelectedIds.size === 0) {
      selectAll()
    } else {
      deselectAll()
    }
  }, [internalSelectedIds, selectAll, deselectAll])

  const toggleRootExpansion = useCallback(() => {
    if (expandedIds.size === 0) {
      expandAll()
    } else {
      collapseAll()
    }
  }, [expandedIds, expandAll, collapseAll])

  const getTotalItemCount = useCallback((items: FilterItem[]): number => {
    return items.reduce((count, item) => {
      return count + 1 + (item.children ? getTotalItemCount(item.children) : 0)
    }, 0)
  }, [])

  const getRootSelectionState = useCallback(() => {
    const totalCount = getTotalItemCount(data)
    const selectedCount = internalSelectedIds.size

    if (selectedCount === 0) return "none"
    if (selectedCount === totalCount) return "all"

    const hasPartialSelection = (items: FilterItem[]): boolean => {
      for (const item of items) {
        if (isPartiallySelected(item)) return true
        if (item.children && hasPartialSelection(item.children)) return true
      }
      return false
    }

    return hasPartialSelection(data) ? "partial" : "all"
  }, [data, internalSelectedIds, getTotalItemCount, isPartiallySelected])

  const rootSelectionState = useMemo(() => getRootSelectionState(), [getRootSelectionState])

  const clearSearch = useCallback(() => {
    setSearchTerm("")
  }, [])

  const highlightSearchTerm = useCallback((text: string, term: string) => {
    if (!term.trim()) return text

    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    const parts = text.split(regex)

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 text-black">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    )
  }, [])

  const updatePath = useCallback(
    (item: FilterItem) => {
      const newPath = [...currentPath]
      const existingIndex = newPath.findIndex((pathItem) => pathItem.id === item.id)
      if (existingIndex !== -1) {
        newPath.splice(existingIndex + 1)
      } else {
        newPath.push({ id: item.id, name: item.name })
      }
      setCurrentPath(newPath)
    },
    [currentPath],
  )

  const navigateTo = useCallback(
    (id: string) => {
      const newPath = currentPath.slice(0, currentPath.findIndex((item) => item.id === id) + 1)
      setCurrentPath(newPath)
      setExpandedIds(new Set(newPath.map((item) => item.id)))
    },
    [currentPath],
  )

  const getTotalFilteredCount = useCallback(
    (items: FilterItem[]): number => {
      return items.reduce((count, item) => {
        const filteredCount = extractFilterCount(filterCounts, item, "filtered")
        return count + filteredCount
      }, 0)
    },
    [filterCounts],
  )

  const getTotalUnfilteredCount = useCallback(
    (items: FilterItem[]): number => {
      return items.reduce((count, item) => {
        const unfilteredCount = extractFilterCount(filterCounts, item, "total")
        return count + unfilteredCount
      }, 0)
    },
    [filterCounts],
  )

  const hasFilteredResults = useMemo(() => {
    return searchTerm.trim() !== "" && filteredData.length > 0 && filteredData.length !== data.length
  }, [searchTerm, filteredData, data])

  const renderItems = useCallback(
    (items: FilterItem[], depth = 0) => {
      return items.map((item) => {
        const isSelected = isItemSelected(item)
        const isPartial = isPartiallySelected(item)
        const isExpanded = expandedIds.has(item.id)
        const hasChildren = item.children && item.children.length > 0

        const isSpecialCase = item.name.startsWith("(No ")
        const itemStyle = isSpecialCase ? "italic text-muted-foreground" : ""

        const unfilteredCount = filterCounts ? extractFilterCount(filterCounts, item, "total") : 0
        const filteredCount = filterCounts ? extractFilterCount(filterCounts, item, "filtered") : 0
        const missingCounts = filterCounts ? extractFilterCount(filterCounts, item, "missingData") : 0
        const qaCount = filterCounts ? extractFilterCount(filterCounts, item, "unmatchedSegments") : 0

        return (
          <div key={item.id} className={cn("ml-1", depth === 0 && "ml-0")}>
            {hasChildren ? (
              <Accordion
                type="single"
                collapsible
                value={isExpanded ? item.id : undefined}
                onValueChange={(value) => {
                  if (value === item.id) {
                    toggleExpand(item.id)
                    updatePath(item)
                  } else {
                    toggleExpand(item.id)
                  }
                }}
              >
                <AccordionItem value={item.id} className="border-none">
                  <div className="flex items-center space-x-1 py-1">
                    <AccordionTrigger
                      className="hover:no-underline py-0 pr-2 flex-none"
                      onClick={(e) => {
                        e.preventDefault()
                        toggleExpand(item.id)
                        updatePath(item)
                      }}
                    >
                    </AccordionTrigger>
                    <FilterCheckbox
                      id={item.id}
                      checked={isSelected}
                      className={cn(isPartial && "bg-primary/25 text-primary-foreground")}
                      onCheckedChange={() => handleSelect(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      isPartial={isPartial}
                    />
                    <ListItem
                      item={item}
                      isSelected={isSelected}
                      counts={extractCountForKey(filteredCounts, item)}
                      className={itemStyle}
                      customComponent={renderCustom && renderCustom(item)}
                      searchTerm={searchTerm}
                      highlightSearchTerm={highlightSearchTerm}
                    />
                  </div>
                  <AccordionContent className="pb-0 pt-1 ml-3">
                    {renderItems(item.children, depth + 1)}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <div className="flex items-center space-x-2 py-2 ml-6">
                <FilterCheckbox
                  id={item.id}
                  checked={isSelected}
                  className={cn(isPartial && "bg-primary/25 text-primary-foreground")}
                  onCheckedChange={() => handleSelect(item.id)}
                  isPartial={false}
                />
                <ListItem
                  item={item}
                  isSelected={isSelected}
                  counts={extractCountForKey(filteredCounts, item)}
                  className={itemStyle}
                  searchTerm={searchTerm}
                  highlightSearchTerm={highlightSearchTerm}
                />
              </div>
            )}
          </div>
        )
      })
    },
    [
      isItemSelected,
      isPartiallySelected,
      expandedIds,
      handleSelect,
      toggleExpand,
      updatePath,
      filteredCounts,
      renderCustom,
      searchTerm,
      highlightSearchTerm,
    ],
  )

  const selectedCount = internalSelectedIds.size
  const totalCount = getTotalItemCount(data)
  const visibleCount = useMemo(() => getTotalItemCount(filteredData), [filteredData, getTotalItemCount])

  const actualSelectedCount = useMemo(() => (selectedFilters[name] ?? []).length, [selectedFilters, name]);

  return (
    <div className="space-y-2">
      <TooltipProvider>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <div
               className={cn(
                  "flex items-center justify-between w-full",
                  "h-9 px-3 py-2",
                  "border border-input bg-background hover:bg-accent hover:text-accent-foreground", 
                  "text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "rounded-md",
                  "cursor-pointer"
               )}
            >
              <div className="flex items-center gap-2">
               
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm font-medium truncate max-w-[200px]">
                      {description}
                      <span className="text-xs text-muted-foreground ml-1">
                        ({actualSelectedCount > 0 ? `${actualSelectedCount} selected` : `${visibleCount} total`})
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{description}</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="flex items-center gap-1">
                {actualSelectedCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          deselectAll();
                        }}
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Clear selection</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Clear selection for {description}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[350px] p-0" align="start" sideOffset={5}>
            <div className="flex flex-col max-h-[80vh]">
              <div className="flex items-center justify-between p-3 border-b">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="text-sm font-medium truncate max-w-[150px]">{description}</h3>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{description}</p>
                  </TooltipContent>
                </Tooltip>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAll}
                    disabled={selectedCount === 0}
                    className="h-7 px-2 text-xs"
                  >
                    Clear
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    disabled={selectedCount === totalCount}
                    className="h-7 px-2 text-xs"
                  >
                    Select All
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="h-7 w-7">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="p-3 border-b">
                <div className="relative">
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-8 text-sm pr-8"
                  />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={clearSearch}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {hasFilteredResults && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{visibleCount} items found</span>
                    <Button variant="outline" size="sm" onClick={selectAllVisible} className="h-6 px-2 text-xs">
                      Select all visible
                    </Button>
                  </div>
                )}
              </div>

              <ScrollArea className="flex-1 h-[300px]">
                <div className="p-2">{renderItems(filteredData)}</div>
              </ScrollArea>

              <div className="flex justify-end gap-2 p-3 border-t">
                <Button variant="secondary" size="sm" onClick={() => setIsOpen(false)} className="h-8 text-xs">
                  Cancel
                </Button>
                <Button size="sm" onClick={() => setIsOpen(false)} className="h-8 text-xs">
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </TooltipProvider>
    </div>
  )
}
